/**
 * A resource type: `label` is the name people read (S3 bucket), `key` the raw
 * type the platform stores (AWS::S3::Bucket). Same shape as REGION_MAP.
 *
 * Labels use the vendor's product name plus a plain noun, in sentence case,
 * without the provider, because a provider icon sits beside the name wherever
 * it shows. Kubernetes labels are the kind as kubectl prints it.
 */
type ResourceType = { provider: string; label: string; key: string };
type ProviderResourceTypeMap = Record<string, ResourceType>;

export const RESOURCE_TYPE_MAP: Record<string, ProviderResourceTypeMap> = {
  AWS: {
    'AWS::AccessAnalyzer::Analyzer': {
      provider: 'AWS',
      label: 'IAM Access Analyzer',
      key: 'AWS::AccessAnalyzer::Analyzer',
    },
    'AWS::AccessAnalyzer::Finding': {
      provider: 'AWS',
      label: 'IAM Access Analyzer finding',
      key: 'AWS::AccessAnalyzer::Finding',
    },
    'AWS::ACM::Certificate': {
      provider: 'AWS',
      label: 'ACM certificate',
      key: 'AWS::ACM::Certificate',
    },
    'AWS::AgentRegistry::Registry': {
      provider: 'AWS',
      label: 'Agent Registry',
      key: 'AWS::AgentRegistry::Registry',
    },
    'AWS::AgentRegistry::RegistryRecord': {
      provider: 'AWS',
      label: 'Agent Registry record',
      key: 'AWS::AgentRegistry::RegistryRecord',
    },
    'AWS::APIGateway::DomainName': {
      provider: 'AWS',
      label: 'API Gateway custom domain',
      key: 'AWS::APIGateway::DomainName',
    },
    'AWS::APIGateway::RestAPI': {
      provider: 'AWS',
      label: 'API Gateway REST API',
      key: 'AWS::APIGateway::RestAPI',
    },
    'AWS::APIGateway::Stage': {
      provider: 'AWS',
      label: 'API Gateway stage',
      key: 'AWS::APIGateway::Stage',
    },
    'AWS::APIGateway::VpcLink': {
      provider: 'AWS',
      label: 'API Gateway VPC link',
      key: 'AWS::APIGateway::VpcLink',
    },
    'AWS::ApiGatewayV2::Api': {
      provider: 'AWS',
      label: 'API Gateway HTTP or WebSocket API',
      key: 'AWS::ApiGatewayV2::Api',
    },
    'AWS::ApplicationAutoScaling::ECSScalingPolicy': {
      provider: 'AWS',
      label: 'ECS service auto scaling policy',
      key: 'AWS::ApplicationAutoScaling::ECSScalingPolicy',
    },
    'AWS::AppMesh::Mesh': {
      provider: 'AWS',
      label: 'App Mesh service mesh',
      key: 'AWS::AppMesh::Mesh',
    },
    'AWS::AppSync::GraphQLApi': {
      provider: 'AWS',
      label: 'AppSync GraphQL API',
      key: 'AWS::AppSync::GraphQLApi',
    },
    'AWS::APS::Workspace': {
      provider: 'AWS',
      label: 'Managed Service for Prometheus workspace',
      key: 'AWS::APS::Workspace',
    },
    'AWS::AutoScaling::AutoScalingGroup': {
      provider: 'AWS',
      label: 'Auto Scaling group',
      key: 'AWS::AutoScaling::AutoScalingGroup',
    },
    'AWS::AutoScaling::LaunchConfiguration': {
      provider: 'AWS',
      label: 'Auto Scaling launch configuration',
      key: 'AWS::AutoScaling::LaunchConfiguration',
    },
    'AWS::AutoScaling::ScalingPolicy': {
      provider: 'AWS',
      label: 'EC2 Auto Scaling policy',
      key: 'AWS::AutoScaling::ScalingPolicy',
    },
    'AWS::Backup::BackupPlan': {
      provider: 'AWS',
      label: 'Backup plan',
      key: 'AWS::Backup::BackupPlan',
    },
    'AWS::Backup::BackupVault': {
      provider: 'AWS',
      label: 'Backup vault',
      key: 'AWS::Backup::BackupVault',
    },
    'AWS::Backup::ProtectedResource': {
      provider: 'AWS',
      label: 'Backup protected resource',
      key: 'AWS::Backup::ProtectedResource',
    },
    'AWS::Bedrock::Agent': {
      provider: 'AWS',
      label: 'Bedrock agent',
      key: 'AWS::Bedrock::Agent',
    },
    'AWS::Bedrock::AgentActionGroup': {
      provider: 'AWS',
      label: 'Bedrock agent action group',
      key: 'AWS::Bedrock::AgentActionGroup',
    },
    'AWS::Bedrock::AutomatedReasoningPolicy': {
      provider: 'AWS',
      label: 'Bedrock Automated Reasoning policy',
      key: 'AWS::Bedrock::AutomatedReasoningPolicy',
    },
    'AWS::Bedrock::Blueprint': {
      provider: 'AWS',
      label: 'Bedrock Data Automation blueprint',
      key: 'AWS::Bedrock::Blueprint',
    },
    'AWS::Bedrock::CustomModel': {
      provider: 'AWS',
      label: 'Bedrock custom model',
      key: 'AWS::Bedrock::CustomModel',
    },
    'AWS::Bedrock::CustomModelDeployment': {
      provider: 'AWS',
      label: 'Bedrock custom model deployment',
      key: 'AWS::Bedrock::CustomModelDeployment',
    },
    'AWS::Bedrock::DataAutomationLibrary': {
      provider: 'AWS',
      label: 'Bedrock Data Automation library',
      key: 'AWS::Bedrock::DataAutomationLibrary',
    },
    'AWS::Bedrock::DataAutomationProject': {
      provider: 'AWS',
      label: 'Bedrock Data Automation project',
      key: 'AWS::Bedrock::DataAutomationProject',
    },
    'AWS::Bedrock::DataSource': {
      provider: 'AWS',
      label: 'Bedrock knowledge base data source',
      key: 'AWS::Bedrock::DataSource',
    },
    'AWS::Bedrock::EnforcedGuardrailsConfiguration': {
      provider: 'AWS',
      label: 'Bedrock enforced guardrail setting',
      key: 'AWS::Bedrock::EnforcedGuardrailsConfiguration',
    },
    'AWS::Bedrock::EvaluationJob': {
      provider: 'AWS',
      label: 'Bedrock evaluation job',
      key: 'AWS::Bedrock::EvaluationJob',
    },
    'AWS::Bedrock::Flow': {
      provider: 'AWS',
      label: 'Bedrock flow',
      key: 'AWS::Bedrock::Flow',
    },
    'AWS::Bedrock::FoundationModel': {
      provider: 'AWS',
      label: 'Bedrock foundation model',
      key: 'AWS::Bedrock::FoundationModel',
    },
    'AWS::Bedrock::Guardrail': {
      provider: 'AWS',
      label: 'Bedrock guardrail',
      key: 'AWS::Bedrock::Guardrail',
    },
    'AWS::Bedrock::ImportedModel': {
      provider: 'AWS',
      label: 'Bedrock imported model',
      key: 'AWS::Bedrock::ImportedModel',
    },
    'AWS::Bedrock::InferenceProfile': {
      provider: 'AWS',
      label: 'Bedrock inference profile',
      key: 'AWS::Bedrock::InferenceProfile',
    },
    'AWS::Bedrock::KnowledgeBase': {
      provider: 'AWS',
      label: 'Bedrock knowledge base',
      key: 'AWS::Bedrock::KnowledgeBase',
    },
    'AWS::Bedrock::MarketplaceModelEndpoint': {
      provider: 'AWS',
      label: 'Bedrock Marketplace model endpoint',
      key: 'AWS::Bedrock::MarketplaceModelEndpoint',
    },
    'AWS::Bedrock::ModelCopyJob': {
      provider: 'AWS',
      label: 'Bedrock model copy job',
      key: 'AWS::Bedrock::ModelCopyJob',
    },
    'AWS::Bedrock::ModelCustomizationJob': {
      provider: 'AWS',
      label: 'Bedrock model customization job',
      key: 'AWS::Bedrock::ModelCustomizationJob',
    },
    'AWS::Bedrock::ModelImportJob': {
      provider: 'AWS',
      label: 'Bedrock model import job',
      key: 'AWS::Bedrock::ModelImportJob',
    },
    'AWS::Bedrock::ModelInvocationJob': {
      provider: 'AWS',
      label: 'Bedrock batch inference job',
      key: 'AWS::Bedrock::ModelInvocationJob',
    },
    'AWS::Bedrock::ModelInvocationLoggingConfiguration': {
      provider: 'AWS',
      label: 'Bedrock model invocation logging setting',
      key: 'AWS::Bedrock::ModelInvocationLoggingConfiguration',
    },
    'AWS::Bedrock::Prompt': {
      provider: 'AWS',
      label: 'Bedrock prompt',
      key: 'AWS::Bedrock::Prompt',
    },
    'AWS::Bedrock::PromptRouter': {
      provider: 'AWS',
      label: 'Bedrock prompt router',
      key: 'AWS::Bedrock::PromptRouter',
    },
    'AWS::Bedrock::ProvisionedModel': {
      provider: 'AWS',
      label: 'Bedrock Provisioned Throughput',
      key: 'AWS::Bedrock::ProvisionedModel',
    },
    'AWS::Bedrock::Session': {
      provider: 'AWS',
      label: 'Bedrock session',
      key: 'AWS::Bedrock::Session',
    },
    'AWS::BedrockAgentCore::ABTest': {
      provider: 'AWS',
      label: 'Bedrock AgentCore A/B test',
      key: 'AWS::BedrockAgentCore::ABTest',
    },
    'AWS::BedrockAgentCore::ApiKeyCredentialProvider': {
      provider: 'AWS',
      label: 'Bedrock AgentCore API key credential provider',
      key: 'AWS::BedrockAgentCore::ApiKeyCredentialProvider',
    },
    'AWS::BedrockAgentCore::BatchEvaluation': {
      provider: 'AWS',
      label: 'Bedrock AgentCore batch evaluation',
      key: 'AWS::BedrockAgentCore::BatchEvaluation',
    },
    'AWS::BedrockAgentCore::Browser': {
      provider: 'AWS',
      label: 'Bedrock AgentCore browser',
      key: 'AWS::BedrockAgentCore::Browser',
    },
    'AWS::BedrockAgentCore::BrowserProfile': {
      provider: 'AWS',
      label: 'Bedrock AgentCore browser profile',
      key: 'AWS::BedrockAgentCore::BrowserProfile',
    },
    'AWS::BedrockAgentCore::CapacityProvider': {
      provider: 'AWS',
      label: 'Bedrock AgentCore capacity provider',
      key: 'AWS::BedrockAgentCore::CapacityProvider',
    },
    'AWS::BedrockAgentCore::CodeInterpreter': {
      provider: 'AWS',
      label: 'Bedrock AgentCore code interpreter',
      key: 'AWS::BedrockAgentCore::CodeInterpreter',
    },
    'AWS::BedrockAgentCore::ConfigurationBundle': {
      provider: 'AWS',
      label: 'Bedrock AgentCore configuration bundle',
      key: 'AWS::BedrockAgentCore::ConfigurationBundle',
    },
    'AWS::BedrockAgentCore::ConsentPortal': {
      provider: 'AWS',
      label: 'Bedrock AgentCore consent portal',
      key: 'AWS::BedrockAgentCore::ConsentPortal',
    },
    'AWS::BedrockAgentCore::Dataset': {
      provider: 'AWS',
      label: 'Bedrock AgentCore dataset',
      key: 'AWS::BedrockAgentCore::Dataset',
    },
    'AWS::BedrockAgentCore::Evaluator': {
      provider: 'AWS',
      label: 'Bedrock AgentCore evaluator',
      key: 'AWS::BedrockAgentCore::Evaluator',
    },
    'AWS::BedrockAgentCore::Gateway': {
      provider: 'AWS',
      label: 'Bedrock AgentCore gateway',
      key: 'AWS::BedrockAgentCore::Gateway',
    },
    'AWS::BedrockAgentCore::GatewayTarget': {
      provider: 'AWS',
      label: 'Bedrock AgentCore gateway target',
      key: 'AWS::BedrockAgentCore::GatewayTarget',
    },
    'AWS::BedrockAgentCore::Harness': {
      provider: 'AWS',
      label: 'Bedrock AgentCore harness',
      key: 'AWS::BedrockAgentCore::Harness',
    },
    'AWS::BedrockAgentCore::Memory': {
      provider: 'AWS',
      label: 'Bedrock AgentCore memory',
      key: 'AWS::BedrockAgentCore::Memory',
    },
    'AWS::BedrockAgentCore::OAuth2CredentialProvider': {
      provider: 'AWS',
      label: 'Bedrock AgentCore OAuth2 credential provider',
      key: 'AWS::BedrockAgentCore::OAuth2CredentialProvider',
    },
    'AWS::BedrockAgentCore::OnlineEvaluationConfig': {
      provider: 'AWS',
      label: 'Bedrock AgentCore online evaluation configuration',
      key: 'AWS::BedrockAgentCore::OnlineEvaluationConfig',
    },
    'AWS::BedrockAgentCore::PaymentConnector': {
      provider: 'AWS',
      label: 'Bedrock AgentCore payment connector',
      key: 'AWS::BedrockAgentCore::PaymentConnector',
    },
    'AWS::BedrockAgentCore::PaymentCredentialProvider': {
      provider: 'AWS',
      label: 'Bedrock AgentCore payment credential provider',
      key: 'AWS::BedrockAgentCore::PaymentCredentialProvider',
    },
    'AWS::BedrockAgentCore::PaymentManager': {
      provider: 'AWS',
      label: 'Bedrock AgentCore payment manager',
      key: 'AWS::BedrockAgentCore::PaymentManager',
    },
    'AWS::BedrockAgentCore::Policy': {
      provider: 'AWS',
      label: 'Bedrock AgentCore policy',
      key: 'AWS::BedrockAgentCore::Policy',
    },
    'AWS::BedrockAgentCore::PolicyEngine': {
      provider: 'AWS',
      label: 'Bedrock AgentCore policy engine',
      key: 'AWS::BedrockAgentCore::PolicyEngine',
    },
    'AWS::BedrockAgentCore::PolicyGeneration': {
      provider: 'AWS',
      label: 'Bedrock AgentCore policy generation',
      key: 'AWS::BedrockAgentCore::PolicyGeneration',
    },
    'AWS::BedrockAgentCore::Registry': {
      provider: 'AWS',
      label: 'Agent Registry',
      key: 'AWS::BedrockAgentCore::Registry',
    },
    'AWS::BedrockAgentCore::RegistryRecord': {
      provider: 'AWS',
      label: 'Agent Registry record',
      key: 'AWS::BedrockAgentCore::RegistryRecord',
    },
    'AWS::BedrockAgentCore::Runtime': {
      provider: 'AWS',
      label: 'Bedrock AgentCore runtime',
      key: 'AWS::BedrockAgentCore::Runtime',
    },
    'AWS::BedrockAgentCore::RuntimeEndpoint': {
      provider: 'AWS',
      label: 'Bedrock AgentCore runtime endpoint',
      key: 'AWS::BedrockAgentCore::RuntimeEndpoint',
    },
    'AWS::BedrockAgentCore::TokenVault': {
      provider: 'AWS',
      label: 'Bedrock AgentCore token vault',
      key: 'AWS::BedrockAgentCore::TokenVault',
    },
    'AWS::BedrockAgentCore::WorkloadIdentity': {
      provider: 'AWS',
      label: 'Bedrock AgentCore workload identity',
      key: 'AWS::BedrockAgentCore::WorkloadIdentity',
    },
    'AWS::Cloudformation::Stack': {
      provider: 'AWS',
      label: 'CloudFormation stack',
      key: 'AWS::Cloudformation::Stack',
    },
    'AWS::CloudFront::Distribution': {
      provider: 'AWS',
      label: 'CloudFront distribution',
      key: 'AWS::CloudFront::Distribution',
    },
    'AWS::CloudFront::VpcOrigin': {
      provider: 'AWS',
      label: 'CloudFront VPC origin',
      key: 'AWS::CloudFront::VpcOrigin',
    },
    'AWS::CloudTrail::Channel': {
      provider: 'AWS',
      label: 'CloudTrail channel',
      key: 'AWS::CloudTrail::Channel',
    },
    'AWS::CloudTrail::EventDataStore': {
      provider: 'AWS',
      label: 'CloudTrail Lake event data store',
      key: 'AWS::CloudTrail::EventDataStore',
    },
    'AWS::CloudTrail::Trail': {
      provider: 'AWS',
      label: 'CloudTrail trail',
      key: 'AWS::CloudTrail::Trail',
    },
    'AWS::CloudWatch::Alarm': {
      provider: 'AWS',
      label: 'CloudWatch alarm',
      key: 'AWS::CloudWatch::Alarm',
    },
    'AWS::CloudWatchLogs::MetricFilter': {
      provider: 'AWS',
      label: 'CloudWatch Logs metric filter',
      key: 'AWS::CloudWatchLogs::MetricFilter',
    },
    'AWS::CloudWatchLogs::ResourcePolicy': {
      provider: 'AWS',
      label: 'CloudWatch Logs resource policy',
      key: 'AWS::CloudWatchLogs::ResourcePolicy',
    },
    'AWS::CodeArtifact::Domain': {
      provider: 'AWS',
      label: 'CodeArtifact domain',
      key: 'AWS::CodeArtifact::Domain',
    },
    'AWS::CodeArtifact::Repository': {
      provider: 'AWS',
      label: 'CodeArtifact repository',
      key: 'AWS::CodeArtifact::Repository',
    },
    'AWS::CodeBuild::Project': {
      provider: 'AWS',
      label: 'CodeBuild project',
      key: 'AWS::CodeBuild::Project',
    },
    'AWS::CodeBuild::ReportGroup': {
      provider: 'AWS',
      label: 'CodeBuild report group',
      key: 'AWS::CodeBuild::ReportGroup',
    },
    'AWS::CodeConnections::Connection': {
      provider: 'AWS',
      label: 'CodeConnections connection',
      key: 'AWS::CodeConnections::Connection',
    },
    'AWS::Cognito::IdentityPool': {
      provider: 'AWS',
      label: 'Cognito identity pool',
      key: 'AWS::Cognito::IdentityPool',
    },
    'AWS::Cognito::UserPool': {
      provider: 'AWS',
      label: 'Cognito user pool',
      key: 'AWS::Cognito::UserPool',
    },
    'AWS::ComputeOptimizer::EnrollmentStatus': {
      provider: 'AWS',
      label: 'Compute Optimizer enrollment status',
      key: 'AWS::ComputeOptimizer::EnrollmentStatus',
    },
    'AWS::Config::ConfigurationRecorder': {
      provider: 'AWS',
      label: 'AWS Config configuration recorder',
      key: 'AWS::Config::ConfigurationRecorder',
    },
    'AWS::Config::ConfigurationRecorderStatus': {
      provider: 'AWS',
      label: 'AWS Config recorder status',
      key: 'AWS::Config::ConfigurationRecorderStatus',
    },
    'AWS::DirectoryService::Directory': {
      provider: 'AWS',
      label: 'Directory Service directory',
      key: 'AWS::DirectoryService::Directory',
    },
    'AWS::DocDB::DBCluster': {
      provider: 'AWS',
      label: 'DocumentDB cluster',
      key: 'AWS::DocDB::DBCluster',
    },
    'AWS::DocDB::DBClusterParameterGroup': {
      provider: 'AWS',
      label: 'DocumentDB cluster parameter group',
      key: 'AWS::DocDB::DBClusterParameterGroup',
    },
    'AWS::DocDB::DBClusterSnapshot': {
      provider: 'AWS',
      label: 'DocumentDB cluster snapshot',
      key: 'AWS::DocDB::DBClusterSnapshot',
    },
    'AWS::DocDB::DBInstance': {
      provider: 'AWS',
      label: 'DocumentDB instance',
      key: 'AWS::DocDB::DBInstance',
    },
    'AWS::DynamoDB::ScalableTarget': {
      provider: 'AWS',
      label: 'DynamoDB auto scaling target',
      key: 'AWS::DynamoDB::ScalableTarget',
    },
    'AWS::DynamoDB::Table': {
      provider: 'AWS',
      label: 'DynamoDB table',
      key: 'AWS::DynamoDB::Table',
    },
    'AWS::EC2::AMI': {
      provider: 'AWS',
      label: 'EC2 AMI',
      key: 'AWS::EC2::AMI',
    },
    'AWS::EC2::DefaultEBSEncryption': {
      provider: 'AWS',
      label: 'EBS default encryption setting',
      key: 'AWS::EC2::DefaultEBSEncryption',
    },
    'AWS::EC2::ElasticIP': {
      provider: 'AWS',
      label: 'Elastic IP address',
      key: 'AWS::EC2::ElasticIP',
    },
    'AWS::EC2::Endpoint': {
      provider: 'AWS',
      label: 'VPC endpoint',
      key: 'AWS::EC2::Endpoint',
    },
    'AWS::EC2::FlowLog': {
      provider: 'AWS',
      label: 'VPC flow log',
      key: 'AWS::EC2::FlowLog',
    },
    'AWS::EC2::FpgaImage': {
      provider: 'AWS',
      label: 'EC2 FPGA image',
      key: 'AWS::EC2::FpgaImage',
    },
    'AWS::EC2::Instance': {
      provider: 'AWS',
      label: 'EC2 instance',
      key: 'AWS::EC2::Instance',
    },
    'AWS::EC2::InternetGateway': {
      provider: 'AWS',
      label: 'Internet gateway',
      key: 'AWS::EC2::InternetGateway',
    },
    'AWS::EC2::IPAMResourceDiscovery': {
      provider: 'AWS',
      label: 'VPC IPAM resource discovery',
      key: 'AWS::EC2::IPAMResourceDiscovery',
    },
    'AWS::EC2::LaunchTemplate': {
      provider: 'AWS',
      label: 'EC2 launch template',
      key: 'AWS::EC2::LaunchTemplate',
    },
    'AWS::EC2::NatGateway': {
      provider: 'AWS',
      label: 'NAT gateway',
      key: 'AWS::EC2::NatGateway',
    },
    'AWS::EC2::NetworkAcl': {
      provider: 'AWS',
      label: 'Network ACL',
      key: 'AWS::EC2::NetworkAcl',
    },
    'AWS::EC2::NetworkInterface': {
      provider: 'AWS',
      label: 'Network interface',
      key: 'AWS::EC2::NetworkInterface',
    },
    'AWS::EC2::PlacementGroup': {
      provider: 'AWS',
      label: 'EC2 placement group',
      key: 'AWS::EC2::PlacementGroup',
    },
    'AWS::EC2::PrefixList': {
      provider: 'AWS',
      label: 'VPC managed prefix list',
      key: 'AWS::EC2::PrefixList',
    },
    'AWS::EC2::RouteTable': {
      provider: 'AWS',
      label: 'Route table',
      key: 'AWS::EC2::RouteTable',
    },
    'AWS::EC2::SecurityGroup': {
      provider: 'AWS',
      label: 'Security group',
      key: 'AWS::EC2::SecurityGroup',
    },
    'AWS::EC2::Snapshot': {
      provider: 'AWS',
      label: 'EBS snapshot',
      key: 'AWS::EC2::Snapshot',
    },
    'AWS::EC2::SnapshotBlockPublicAccess': {
      provider: 'AWS',
      label: 'EBS snapshot block public access setting',
      key: 'AWS::EC2::SnapshotBlockPublicAccess',
    },
    'AWS::EC2::Subnet': {
      provider: 'AWS',
      label: 'Subnet',
      key: 'AWS::EC2::Subnet',
    },
    'AWS::EC2::TransitGateway': {
      provider: 'AWS',
      label: 'Transit gateway',
      key: 'AWS::EC2::TransitGateway',
    },
    'AWS::EC2::TransitGatewayMulticastDomain': {
      provider: 'AWS',
      label: 'Transit gateway multicast domain',
      key: 'AWS::EC2::TransitGatewayMulticastDomain',
    },
    'AWS::EC2::Volume': {
      provider: 'AWS',
      label: 'EBS volume',
      key: 'AWS::EC2::Volume',
    },
    'AWS::EC2::VPC': { provider: 'AWS', label: 'VPC', key: 'AWS::EC2::VPC' },
    'AWS::EC2::VpcPeeringConnection': {
      provider: 'AWS',
      label: 'VPC peering connection',
      key: 'AWS::EC2::VpcPeeringConnection',
    },
    'AWS::EC2::VPN': {
      provider: 'AWS',
      label: 'Site-to-Site VPN connection',
      key: 'AWS::EC2::VPN',
    },
    'AWS::ECR::ContainerImage': {
      provider: 'AWS',
      label: 'ECR container image',
      key: 'AWS::ECR::ContainerImage',
    },
    'AWS::ECR::Registry': {
      provider: 'AWS',
      label: 'ECR private registry',
      key: 'AWS::ECR::Registry',
    },
    'AWS::ECR::Repository': {
      provider: 'AWS',
      label: 'ECR repository',
      key: 'AWS::ECR::Repository',
    },
    'AWS::ECS::Cluster': {
      provider: 'AWS',
      label: 'ECS cluster',
      key: 'AWS::ECS::Cluster',
    },
    'AWS::ECS::Service': {
      provider: 'AWS',
      label: 'ECS service',
      key: 'AWS::ECS::Service',
    },
    'AWS::ECS::TaskDefinition': {
      provider: 'AWS',
      label: 'ECS task definition',
      key: 'AWS::ECS::TaskDefinition',
    },
    'AWS::EFS::FileSystem': {
      provider: 'AWS',
      label: 'EFS file system',
      key: 'AWS::EFS::FileSystem',
    },
    'AWS::EKS::Cluster': {
      provider: 'AWS',
      label: 'EKS cluster',
      key: 'AWS::EKS::Cluster',
    },
    'AWS::EKS::NodeGroup': {
      provider: 'AWS',
      label: 'EKS managed node group',
      key: 'AWS::EKS::NodeGroup',
    },
    'AWS::ElastiCache::CacheCluster': {
      provider: 'AWS',
      label: 'ElastiCache cluster',
      key: 'AWS::ElastiCache::CacheCluster',
    },
    'AWS::ElastiCache::ReplicationGroup': {
      provider: 'AWS',
      label: 'ElastiCache replication group',
      key: 'AWS::ElastiCache::ReplicationGroup',
    },
    'AWS::ElasticLoadBalancing::LoadBalancer': {
      provider: 'AWS',
      label: 'Classic Load Balancer',
      key: 'AWS::ElasticLoadBalancing::LoadBalancer',
    },
    'AWS::ElasticLoadBalancingV2::LoadBalancer': {
      provider: 'AWS',
      label: 'ELBv2 load balancer',
      key: 'AWS::ElasticLoadBalancingV2::LoadBalancer',
    },
    'AWS::ElasticLoadBalancingV2::TargetGroup': {
      provider: 'AWS',
      label: 'Load balancer target group',
      key: 'AWS::ElasticLoadBalancingV2::TargetGroup',
    },
    'AWS::EMR::Cluster': {
      provider: 'AWS',
      label: 'EMR cluster',
      key: 'AWS::EMR::Cluster',
    },
    'AWS::EMR::PublicAccessConfiguration': {
      provider: 'AWS',
      label: 'EMR block public access setting',
      key: 'AWS::EMR::PublicAccessConfiguration',
    },
    'AWS::EMR::Studio': {
      provider: 'AWS',
      label: 'EMR Studio',
      key: 'AWS::EMR::Studio',
    },
    'AWS::EventBridge::EventBus': {
      provider: 'AWS',
      label: 'EventBridge event bus',
      key: 'AWS::EventBridge::EventBus',
    },
    'AWS::Glacier::Vault': {
      provider: 'AWS',
      label: 'S3 Glacier vault',
      key: 'AWS::Glacier::Vault',
    },
    'AWS::Glue::Crawler': {
      provider: 'AWS',
      label: 'Glue crawler',
      key: 'AWS::Glue::Crawler',
    },
    'AWS::Glue::Database': {
      provider: 'AWS',
      label: 'Glue database',
      key: 'AWS::Glue::Database',
    },
    'AWS::Glue::DataCatalog': {
      provider: 'AWS',
      label: 'Glue Data Catalog',
      key: 'AWS::Glue::DataCatalog',
    },
    'AWS::Glue::DataCatalogEncryptionSetting': {
      provider: 'AWS',
      label: 'Glue Data Catalog encryption setting',
      key: 'AWS::Glue::DataCatalogEncryptionSetting',
    },
    'AWS::Glue::Job': {
      provider: 'AWS',
      label: 'Glue job',
      key: 'AWS::Glue::Job',
    },
    'AWS::Glue::ResourcePolicy': {
      provider: 'AWS',
      label: 'Glue resource policy',
      key: 'AWS::Glue::ResourcePolicy',
    },
    'AWS::Glue::SecurityConfiguration': {
      provider: 'AWS',
      label: 'Glue security configuration',
      key: 'AWS::Glue::SecurityConfiguration',
    },
    'AWS::GuardDuty::Detector': {
      provider: 'AWS',
      label: 'GuardDuty detector',
      key: 'AWS::GuardDuty::Detector',
    },
    'AWS::IAM::AccountPasswordPolicy': {
      provider: 'AWS',
      label: 'IAM account password policy',
      key: 'AWS::IAM::AccountPasswordPolicy',
    },
    'AWS::IAM::AccountSummary': {
      provider: 'AWS',
      label: 'IAM account summary',
      key: 'AWS::IAM::AccountSummary',
    },
    'AWS::IAM::CredentialReport': {
      provider: 'AWS',
      label: 'IAM credential report',
      key: 'AWS::IAM::CredentialReport',
    },
    'AWS::IAM::Group': {
      provider: 'AWS',
      label: 'IAM group',
      key: 'AWS::IAM::Group',
    },
    'AWS::IAM::InstanceProfile': {
      provider: 'AWS',
      label: 'IAM instance profile',
      key: 'AWS::IAM::InstanceProfile',
    },
    'AWS::IAM::OIDCProvider': {
      provider: 'AWS',
      label: 'IAM OIDC identity provider',
      key: 'AWS::IAM::OIDCProvider',
    },
    'AWS::IAM::Policy': {
      provider: 'AWS',
      label: 'IAM policy',
      key: 'AWS::IAM::Policy',
    },
    'AWS::IAM::Role': {
      provider: 'AWS',
      label: 'IAM role',
      key: 'AWS::IAM::Role',
    },
    'AWS::IAM::SAMLProvider': {
      provider: 'AWS',
      label: 'IAM SAML identity provider',
      key: 'AWS::IAM::SAMLProvider',
    },
    'AWS::IAM::ServerCertificate': {
      provider: 'AWS',
      label: 'IAM server certificate',
      key: 'AWS::IAM::ServerCertificate',
    },
    'AWS::IAM::User': {
      provider: 'AWS',
      label: 'IAM user',
      key: 'AWS::IAM::User',
    },
    'AWS::IAM::VirtualMFADevice': {
      provider: 'AWS',
      label: 'IAM virtual MFA device',
      key: 'AWS::IAM::VirtualMFADevice',
    },
    'AWS::IdentityStore::Group': {
      provider: 'AWS',
      label: 'IAM Identity Center group',
      key: 'AWS::IdentityStore::Group',
    },
    'AWS::IdentityStore::User': {
      provider: 'AWS',
      label: 'IAM Identity Center user',
      key: 'AWS::IdentityStore::User',
    },
    'AWS::ImageBuilder::Component': {
      provider: 'AWS',
      label: 'Image Builder component',
      key: 'AWS::ImageBuilder::Component',
    },
    'AWS::ImageBuilder::ContainerRecipe': {
      provider: 'AWS',
      label: 'Image Builder container recipe',
      key: 'AWS::ImageBuilder::ContainerRecipe',
    },
    'AWS::ImageBuilder::ImageRecipe': {
      provider: 'AWS',
      label: 'Image Builder image recipe',
      key: 'AWS::ImageBuilder::ImageRecipe',
    },
    'AWS::InspectorV2::Account': {
      provider: 'AWS',
      label: 'Inspector account status',
      key: 'AWS::InspectorV2::Account',
    },
    'AWS::Kinesis::Channel': {
      provider: 'AWS',
      label: 'Kinesis data delivery channel',
      key: 'AWS::Kinesis::Channel',
    },
    'AWS::Kinesis::FirehoseDeliveryStream': {
      provider: 'AWS',
      label: 'Data Firehose stream',
      key: 'AWS::Kinesis::FirehoseDeliveryStream',
    },
    'AWS::Kinesis::Stream': {
      provider: 'AWS',
      label: 'Kinesis data stream',
      key: 'AWS::Kinesis::Stream',
    },
    'AWS::KMS::Key': {
      provider: 'AWS',
      label: 'KMS key',
      key: 'AWS::KMS::Key',
    },
    'AWS::Lambda::Function': {
      provider: 'AWS',
      label: 'Lambda function',
      key: 'AWS::Lambda::Function',
    },
    'AWS::Lambda::Layer': {
      provider: 'AWS',
      label: 'Lambda layer',
      key: 'AWS::Lambda::Layer',
    },
    'AWS::Lambda::WebFunction': {
      provider: 'AWS',
      label: 'Lambda web function',
      key: 'AWS::Lambda::WebFunction',
    },
    'AWS::Lightsail::Database': {
      provider: 'AWS',
      label: 'Lightsail database',
      key: 'AWS::Lightsail::Database',
    },
    'AWS::Lightsail::Instance': {
      provider: 'AWS',
      label: 'Lightsail instance',
      key: 'AWS::Lightsail::Instance',
    },
    'AWS::Logs::Delivery': {
      provider: 'AWS',
      label: 'CloudWatch Logs delivery',
      key: 'AWS::Logs::Delivery',
    },
    'AWS::Logs::DeliveryDestination': {
      provider: 'AWS',
      label: 'CloudWatch Logs delivery destination',
      key: 'AWS::Logs::DeliveryDestination',
    },
    'AWS::Logs::Destination': {
      provider: 'AWS',
      label: 'CloudWatch Logs subscription destination',
      key: 'AWS::Logs::Destination',
    },
    'AWS::Logs::LogGroup': {
      provider: 'AWS',
      label: 'CloudWatch log group',
      key: 'AWS::Logs::LogGroup',
    },
    'AWS::Macie::Finding': {
      provider: 'AWS',
      label: 'Macie finding',
      key: 'AWS::Macie::Finding',
    },
    'AWS::Macie::Session': {
      provider: 'AWS',
      label: 'Macie account status',
      key: 'AWS::Macie::Session',
    },
    'AWS::MediaStore::Container': {
      provider: 'AWS',
      label: 'MediaStore container',
      key: 'AWS::MediaStore::Container',
    },
    'AWS::MediaTailor::Channel': {
      provider: 'AWS',
      label: 'MediaTailor channel',
      key: 'AWS::MediaTailor::Channel',
    },
    'AWS::MemoryDB::ACL': {
      provider: 'AWS',
      label: 'MemoryDB ACL',
      key: 'AWS::MemoryDB::ACL',
    },
    'AWS::MemoryDB::Cluster': {
      provider: 'AWS',
      label: 'MemoryDB cluster',
      key: 'AWS::MemoryDB::Cluster',
    },
    'AWS::MemoryDB::User': {
      provider: 'AWS',
      label: 'MemoryDB user',
      key: 'AWS::MemoryDB::User',
    },
    'AWS::MQ::Broker': {
      provider: 'AWS',
      label: 'Amazon MQ broker',
      key: 'AWS::MQ::Broker',
    },
    'AWS::MSK::Cluster': {
      provider: 'AWS',
      label: 'MSK cluster',
      key: 'AWS::MSK::Cluster',
    },
    'AWS::Neptune::DBCluster': {
      provider: 'AWS',
      label: 'Neptune cluster',
      key: 'AWS::Neptune::DBCluster',
    },
    'AWS::Neptune::DBClusterParameterGroup': {
      provider: 'AWS',
      label: 'Neptune cluster parameter group',
      key: 'AWS::Neptune::DBClusterParameterGroup',
    },
    'AWS::Neptune::DBClusterSnapshot': {
      provider: 'AWS',
      label: 'Neptune cluster snapshot',
      key: 'AWS::Neptune::DBClusterSnapshot',
    },
    'AWS::Neptune::DBInstance': {
      provider: 'AWS',
      label: 'Neptune instance',
      key: 'AWS::Neptune::DBInstance',
    },
    'AWS::NetworkFirewall::Firewall': {
      provider: 'AWS',
      label: 'Network Firewall firewall',
      key: 'AWS::NetworkFirewall::Firewall',
    },
    'AWS::NetworkFirewall::FirewallPolicy': {
      provider: 'AWS',
      label: 'Network Firewall policy',
      key: 'AWS::NetworkFirewall::FirewallPolicy',
    },
    'AWS::NetworkFirewall::RuleGroup': {
      provider: 'AWS',
      label: 'Network Firewall rule group',
      key: 'AWS::NetworkFirewall::RuleGroup',
    },
    'AWS::Oam::Sink': {
      provider: 'AWS',
      label: 'Cross-account observability sink',
      key: 'AWS::Oam::Sink',
    },
    'AWS::Omics::SequenceStore': {
      provider: 'AWS',
      label: 'HealthOmics sequence store',
      key: 'AWS::Omics::SequenceStore',
    },
    'AWS::OpenSearch::Domain': {
      provider: 'AWS',
      label: 'OpenSearch domain',
      key: 'AWS::OpenSearch::Domain',
    },
    'AWS::Organizations::Account': {
      provider: 'AWS',
      label: 'AWS Organizations account',
      key: 'AWS::Organizations::Account',
    },
    'AWS::Organizations::DelegatedAdministrator': {
      provider: 'AWS',
      label: 'AWS Organizations delegated administrator',
      key: 'AWS::Organizations::DelegatedAdministrator',
    },
    'AWS::Organizations::EffectivePolicy': {
      provider: 'AWS',
      label: 'AWS Organizations effective policy',
      key: 'AWS::Organizations::EffectivePolicy',
    },
    'AWS::Organizations::Organization': {
      provider: 'AWS',
      label: 'Organization',
      key: 'AWS::Organizations::Organization',
    },
    'AWS::Organizations::OrganizationalUnit': {
      provider: 'AWS',
      label: 'Organizational unit',
      key: 'AWS::Organizations::OrganizationalUnit',
    },
    'AWS::Organizations::ResourceControlPolicy': {
      provider: 'AWS',
      label: 'Resource control policy',
      key: 'AWS::Organizations::ResourceControlPolicy',
    },
    'AWS::Organizations::ServiceControlPolicy': {
      provider: 'AWS',
      label: 'Service control policy',
      key: 'AWS::Organizations::ServiceControlPolicy',
    },
    'AWS::Plerion::AppConfigs': {
      provider: 'AWS',
      label: 'Plerion app configuration',
      key: 'AWS::Plerion::AppConfigs',
    },
    'AWS::Plerion::Integration': {
      provider: 'AWS',
      label: 'Plerion integration',
      key: 'AWS::Plerion::Integration',
    },
    'AWS::Plerion::Integrations': {
      provider: 'AWS',
      label: 'Plerion integration',
      key: 'AWS::Plerion::Integrations',
    },
    'AWS::RAM::ResourceShare': {
      provider: 'AWS',
      label: 'RAM resource share',
      key: 'AWS::RAM::ResourceShare',
    },
    'AWS::RDS::DBCluster': {
      provider: 'AWS',
      label: 'RDS cluster',
      key: 'AWS::RDS::DBCluster',
    },
    'AWS::RDS::DBClusterSnapshot': {
      provider: 'AWS',
      label: 'RDS cluster snapshot',
      key: 'AWS::RDS::DBClusterSnapshot',
    },
    'AWS::RDS::DBInstance': {
      provider: 'AWS',
      label: 'RDS instance',
      key: 'AWS::RDS::DBInstance',
    },
    'AWS::RDS::DBInstanceSnapshot': {
      provider: 'AWS',
      label: 'RDS instance snapshot',
      key: 'AWS::RDS::DBInstanceSnapshot',
    },
    'AWS::RDS::DBProxy': {
      provider: 'AWS',
      label: 'RDS proxy',
      key: 'AWS::RDS::DBProxy',
    },
    'AWS::RDS::DBSecurityGroup': {
      provider: 'AWS',
      label: 'RDS DB security group',
      key: 'AWS::RDS::DBSecurityGroup',
    },
    'AWS::RDS::DBSnapshot': {
      provider: 'AWS',
      label: 'RDS instance snapshot',
      key: 'AWS::RDS::DBSnapshot',
    },
    'AWS::Redshift::Cluster': {
      provider: 'AWS',
      label: 'Redshift cluster',
      key: 'AWS::Redshift::Cluster',
    },
    'AWS::Redshift::ClusterSnapshot': {
      provider: 'AWS',
      label: 'Redshift cluster snapshot',
      key: 'AWS::Redshift::ClusterSnapshot',
    },
    'AWS::Redshift::Namespace': {
      provider: 'AWS',
      label: 'Redshift Serverless namespace',
      key: 'AWS::Redshift::Namespace',
    },
    'AWS::Redshift::ParameterGroup': {
      provider: 'AWS',
      label: 'Redshift parameter group',
      key: 'AWS::Redshift::ParameterGroup',
    },
    'AWS::RedshiftServerless::Snapshot': {
      provider: 'AWS',
      label: 'Redshift Serverless snapshot',
      key: 'AWS::RedshiftServerless::Snapshot',
    },
    'AWS::Rekognition::Project': {
      provider: 'AWS',
      label: 'Rekognition Custom Labels project',
      key: 'AWS::Rekognition::Project',
    },
    'AWS::ResourceGroups::ResourceGroup': {
      provider: 'AWS',
      label: 'Resource group',
      key: 'AWS::ResourceGroups::ResourceGroup',
    },
    'AWS::RolesAnywhere::Profile': {
      provider: 'AWS',
      label: 'IAM Roles Anywhere profile',
      key: 'AWS::RolesAnywhere::Profile',
    },
    'AWS::RolesAnywhere::TrustAnchor': {
      provider: 'AWS',
      label: 'IAM Roles Anywhere trust anchor',
      key: 'AWS::RolesAnywhere::TrustAnchor',
    },
    'AWS::Route53::HostedZone': {
      provider: 'AWS',
      label: 'Route 53 hosted zone',
      key: 'AWS::Route53::HostedZone',
    },
    'AWS::Route53Profiles::Profile': {
      provider: 'AWS',
      label: 'Route 53 profile',
      key: 'AWS::Route53Profiles::Profile',
    },
    'AWS::RUM::AppMonitor': {
      provider: 'AWS',
      label: 'CloudWatch RUM app monitor',
      key: 'AWS::RUM::AppMonitor',
    },
    'AWS::S3::AccessGrantsInstance': {
      provider: 'AWS',
      label: 'S3 Access Grants instance',
      key: 'AWS::S3::AccessGrantsInstance',
    },
    'AWS::S3::AccessPoint': {
      provider: 'AWS',
      label: 'S3 access point',
      key: 'AWS::S3::AccessPoint',
    },
    'AWS::S3::Bucket': {
      provider: 'AWS',
      label: 'S3 bucket',
      key: 'AWS::S3::Bucket',
    },
    'AWS::S3::FileSystem': {
      provider: 'AWS',
      label: 'S3 Files file system',
      key: 'AWS::S3::FileSystem',
    },
    'AWS::S3::MultiRegionAccessPoint': {
      provider: 'AWS',
      label: 'S3 multi-region access point',
      key: 'AWS::S3::MultiRegionAccessPoint',
    },
    'AWS::S3Control::PublicAccessBlock': {
      provider: 'AWS',
      label: 'S3 Block Public Access account setting',
      key: 'AWS::S3Control::PublicAccessBlock',
    },
    'AWS::S3Express::AccessPoint': {
      provider: 'AWS',
      label: 'S3 directory bucket access point',
      key: 'AWS::S3Express::AccessPoint',
    },
    'AWS::S3Express::DirectoryBucket': {
      provider: 'AWS',
      label: 'S3 directory bucket',
      key: 'AWS::S3Express::DirectoryBucket',
    },
    'AWS::S3Tables::Table': {
      provider: 'AWS',
      label: 'S3 table',
      key: 'AWS::S3Tables::Table',
    },
    'AWS::S3Tables::TableBucket': {
      provider: 'AWS',
      label: 'S3 table bucket',
      key: 'AWS::S3Tables::TableBucket',
    },
    'AWS::S3Vectors::VectorBucket': {
      provider: 'AWS',
      label: 'S3 vector bucket',
      key: 'AWS::S3Vectors::VectorBucket',
    },
    'AWS::SageMaker::Algorithm': {
      provider: 'AWS',
      label: 'SageMaker algorithm',
      key: 'AWS::SageMaker::Algorithm',
    },
    'AWS::SageMaker::AutoMLJob': {
      provider: 'AWS',
      label: 'SageMaker AutoML job',
      key: 'AWS::SageMaker::AutoMLJob',
    },
    'AWS::SageMaker::Catalog': {
      provider: 'AWS',
      label: 'SageMaker Feature Store catalog',
      key: 'AWS::SageMaker::Catalog',
    },
    'AWS::SageMaker::Cluster': {
      provider: 'AWS',
      label: 'SageMaker HyperPod cluster',
      key: 'AWS::SageMaker::Cluster',
    },
    'AWS::SageMaker::CompilationJob': {
      provider: 'AWS',
      label: 'SageMaker compilation job',
      key: 'AWS::SageMaker::CompilationJob',
    },
    'AWS::SageMaker::DataQualityJobDefinition': {
      provider: 'AWS',
      label: 'SageMaker data quality job definition',
      key: 'AWS::SageMaker::DataQualityJobDefinition',
    },
    'AWS::SageMaker::Domain': {
      provider: 'AWS',
      label: 'SageMaker domain',
      key: 'AWS::SageMaker::Domain',
    },
    'AWS::SageMaker::EarthObservationJob': {
      provider: 'AWS',
      label: 'SageMaker earth observation job',
      key: 'AWS::SageMaker::EarthObservationJob',
    },
    'AWS::SageMaker::Endpoint': {
      provider: 'AWS',
      label: 'SageMaker endpoint',
      key: 'AWS::SageMaker::Endpoint',
    },
    'AWS::SageMaker::EndpointConfig': {
      provider: 'AWS',
      label: 'SageMaker endpoint configuration',
      key: 'AWS::SageMaker::EndpointConfig',
    },
    'AWS::SageMaker::ExplainabilityJobDefinition': {
      provider: 'AWS',
      label: 'SageMaker explainability job definition',
      key: 'AWS::SageMaker::ExplainabilityJobDefinition',
    },
    'AWS::SageMaker::FeatureGroup': {
      provider: 'AWS',
      label: 'SageMaker feature group',
      key: 'AWS::SageMaker::FeatureGroup',
    },
    'AWS::SageMaker::FlowDefinition': {
      provider: 'AWS',
      label: 'SageMaker human review workflow',
      key: 'AWS::SageMaker::FlowDefinition',
    },
    'AWS::SageMaker::Hub': {
      provider: 'AWS',
      label: 'SageMaker JumpStart hub',
      key: 'AWS::SageMaker::Hub',
    },
    'AWS::SageMaker::HyperParameterTuningJob': {
      provider: 'AWS',
      label: 'SageMaker hyperparameter tuning job',
      key: 'AWS::SageMaker::HyperParameterTuningJob',
    },
    'AWS::SageMaker::Image': {
      provider: 'AWS',
      label: 'SageMaker image',
      key: 'AWS::SageMaker::Image',
    },
    'AWS::SageMaker::InferenceExperiment': {
      provider: 'AWS',
      label: 'SageMaker inference experiment',
      key: 'AWS::SageMaker::InferenceExperiment',
    },
    'AWS::SageMaker::InferenceRecommendationsJob': {
      provider: 'AWS',
      label: 'SageMaker Inference Recommender job',
      key: 'AWS::SageMaker::InferenceRecommendationsJob',
    },
    'AWS::SageMaker::LabelingJob': {
      provider: 'AWS',
      label: 'SageMaker Ground Truth labeling job',
      key: 'AWS::SageMaker::LabelingJob',
    },
    'AWS::SageMaker::MlflowTrackingServer': {
      provider: 'AWS',
      label: 'SageMaker MLflow tracking server',
      key: 'AWS::SageMaker::MlflowTrackingServer',
    },
    'AWS::SageMaker::Model': {
      provider: 'AWS',
      label: 'SageMaker model',
      key: 'AWS::SageMaker::Model',
    },
    'AWS::SageMaker::ModelBiasJobDefinition': {
      provider: 'AWS',
      label: 'SageMaker model bias job definition',
      key: 'AWS::SageMaker::ModelBiasJobDefinition',
    },
    'AWS::SageMaker::ModelCard': {
      provider: 'AWS',
      label: 'SageMaker model card',
      key: 'AWS::SageMaker::ModelCard',
    },
    'AWS::SageMaker::ModelPackage': {
      provider: 'AWS',
      label: 'SageMaker model package',
      key: 'AWS::SageMaker::ModelPackage',
    },
    'AWS::SageMaker::ModelPackageGroup': {
      provider: 'AWS',
      label: 'SageMaker model package group',
      key: 'AWS::SageMaker::ModelPackageGroup',
    },
    'AWS::SageMaker::ModelQualityJobDefinition': {
      provider: 'AWS',
      label: 'SageMaker model quality job definition',
      key: 'AWS::SageMaker::ModelQualityJobDefinition',
    },
    'AWS::SageMaker::MonitoringSchedule': {
      provider: 'AWS',
      label: 'SageMaker monitoring schedule',
      key: 'AWS::SageMaker::MonitoringSchedule',
    },
    'AWS::SageMaker::Notebook': {
      provider: 'AWS',
      label: 'SageMaker notebook instance',
      key: 'AWS::SageMaker::Notebook',
    },
    'AWS::SageMaker::OptimizationJob': {
      provider: 'AWS',
      label: 'SageMaker inference optimization job',
      key: 'AWS::SageMaker::OptimizationJob',
    },
    'AWS::SageMaker::Pipeline': {
      provider: 'AWS',
      label: 'SageMaker pipeline',
      key: 'AWS::SageMaker::Pipeline',
    },
    'AWS::SageMaker::ProcessingJob': {
      provider: 'AWS',
      label: 'SageMaker processing job',
      key: 'AWS::SageMaker::ProcessingJob',
    },
    'AWS::SageMaker::TrainingJob': {
      provider: 'AWS',
      label: 'SageMaker training job',
      key: 'AWS::SageMaker::TrainingJob',
    },
    'AWS::SageMaker::TransformJob': {
      provider: 'AWS',
      label: 'SageMaker batch transform job',
      key: 'AWS::SageMaker::TransformJob',
    },
    'AWS::SageMaker::UserProfile': {
      provider: 'AWS',
      label: 'SageMaker user profile',
      key: 'AWS::SageMaker::UserProfile',
    },
    'AWS::SageMaker::VectorEnrichmentJob': {
      provider: 'AWS',
      label: 'SageMaker vector enrichment job',
      key: 'AWS::SageMaker::VectorEnrichmentJob',
    },
    'AWS::Schemas::Registry': {
      provider: 'AWS',
      label: 'EventBridge schema registry',
      key: 'AWS::Schemas::Registry',
    },
    'AWS::SecretsManager::Secret': {
      provider: 'AWS',
      label: 'Secrets Manager secret',
      key: 'AWS::SecretsManager::Secret',
    },
    'AWS::SecurityHub::Hub': {
      provider: 'AWS',
      label: 'Security Hub account status',
      key: 'AWS::SecurityHub::Hub',
    },
    'AWS::SecurityHub::StandardSubscription': {
      provider: 'AWS',
      label: 'Security Hub standard subscription',
      key: 'AWS::SecurityHub::StandardSubscription',
    },
    'AWS::ServerlessApplicationRepository::Application': {
      provider: 'AWS',
      label: 'Serverless Application Repository application',
      key: 'AWS::ServerlessApplicationRepository::Application',
    },
    'AWS::ServiceCatalogAppRegistry::Application': {
      provider: 'AWS',
      label: 'AppRegistry application',
      key: 'AWS::ServiceCatalogAppRegistry::Application',
    },
    'AWS::ServiceCatalogAppRegistry::AttributeGroup': {
      provider: 'AWS',
      label: 'AppRegistry attribute group',
      key: 'AWS::ServiceCatalogAppRegistry::AttributeGroup',
    },
    'AWS::ServiceDiscovery::HttpNamespace': {
      provider: 'AWS',
      label: 'Cloud Map HTTP namespace',
      key: 'AWS::ServiceDiscovery::HttpNamespace',
    },
    'AWS::ServiceQuotas::ServiceQuota': {
      provider: 'AWS',
      label: 'Service quota',
      key: 'AWS::ServiceQuotas::ServiceQuota',
    },
    'AWS::SES::EmailIdentity': {
      provider: 'AWS',
      label: 'SES email identity',
      key: 'AWS::SES::EmailIdentity',
    },
    'AWS::Signer::SigningProfile': {
      provider: 'AWS',
      label: 'Signing profile',
      key: 'AWS::Signer::SigningProfile',
    },
    'AWS::Signin::ConsoleAuthorizationConfiguration': {
      provider: 'AWS',
      label: 'Console sign-in authorization setting',
      key: 'AWS::Signin::ConsoleAuthorizationConfiguration',
    },
    'AWS::SNS::Subscription': {
      provider: 'AWS',
      label: 'SNS subscription',
      key: 'AWS::SNS::Subscription',
    },
    'AWS::SNS::Topic': {
      provider: 'AWS',
      label: 'SNS topic',
      key: 'AWS::SNS::Topic',
    },
    'AWS::SQS::Queue': {
      provider: 'AWS',
      label: 'SQS queue',
      key: 'AWS::SQS::Queue',
    },
    'AWS::SSM::Document': {
      provider: 'AWS',
      label: 'Systems Manager document',
      key: 'AWS::SSM::Document',
    },
    'AWS::SSM::Parameter': {
      provider: 'AWS',
      label: 'Systems Manager parameter',
      key: 'AWS::SSM::Parameter',
    },
    'AWS::SSO::Instance': {
      provider: 'AWS',
      label: 'IAM Identity Center instance',
      key: 'AWS::SSO::Instance',
    },
    'AWS::SSO::PermissionSet': {
      provider: 'AWS',
      label: 'IAM Identity Center permission set',
      key: 'AWS::SSO::PermissionSet',
    },
    'AWS::VerifiedPermissions::PolicyStore': {
      provider: 'AWS',
      label: 'Verified Permissions policy store',
      key: 'AWS::VerifiedPermissions::PolicyStore',
    },
    'AWS::VpcLattice::ResourceConfiguration': {
      provider: 'AWS',
      label: 'VPC Lattice resource configuration',
      key: 'AWS::VpcLattice::ResourceConfiguration',
    },
    'AWS::VpcLattice::Service': {
      provider: 'AWS',
      label: 'VPC Lattice service',
      key: 'AWS::VpcLattice::Service',
    },
    'AWS::VpcLattice::ServiceNetwork': {
      provider: 'AWS',
      label: 'VPC Lattice service network',
      key: 'AWS::VpcLattice::ServiceNetwork',
    },
    'AWS::WAF::RegionalWebACL': {
      provider: 'AWS',
      label: 'WAF Classic regional web ACL',
      key: 'AWS::WAF::RegionalWebACL',
    },
    'AWS::WAF::RuleGroup': {
      provider: 'AWS',
      label: 'WAF Classic rule group',
      key: 'AWS::WAF::RuleGroup',
    },
    'AWS::WAFRegional::RuleGroup': {
      provider: 'AWS',
      label: 'WAF Classic regional rule group',
      key: 'AWS::WAFRegional::RuleGroup',
    },
    'AWS::WAFv2::RuleGroup': {
      provider: 'AWS',
      label: 'WAF rule group',
      key: 'AWS::WAFv2::RuleGroup',
    },
    'AWS::WAFv2::WebACL': {
      provider: 'AWS',
      label: 'WAF web ACL',
      key: 'AWS::WAFv2::WebACL',
    },
    'AWS::XRay::ResourcePolicy': {
      provider: 'AWS',
      label: 'X-Ray resource policy',
      key: 'AWS::XRay::ResourcePolicy',
    },
  },
  Azure: {
    'Azure::AccessControl::Role': {
      provider: 'Azure',
      label: 'RBAC role definition',
      key: 'Azure::AccessControl::Role',
    },
    'Azure::AccessControl::RoleAssignment': {
      provider: 'Azure',
      label: 'RBAC role assignment',
      key: 'Azure::AccessControl::RoleAssignment',
    },
    'Azure::ActiveDirectory::BuiltInRoleDefinition': {
      provider: 'Azure',
      label: 'Entra ID role definition',
      key: 'Azure::ActiveDirectory::BuiltInRoleDefinition',
    },
    'Azure::ActiveDirectory::Group': {
      provider: 'Azure',
      label: 'Entra ID group',
      key: 'Azure::ActiveDirectory::Group',
    },
    'Azure::ActiveDirectory::ManagementGroup': {
      provider: 'Azure',
      label: 'Management group',
      key: 'Azure::ActiveDirectory::ManagementGroup',
    },
    'Azure::ActiveDirectory::Role': {
      provider: 'Azure',
      label: 'Entra ID custom role definition',
      key: 'Azure::ActiveDirectory::Role',
    },
    'Azure::ActiveDirectory::ServicePrincipal': {
      provider: 'Azure',
      label: 'Entra ID service principal',
      key: 'Azure::ActiveDirectory::ServicePrincipal',
    },
    'Azure::ActiveDirectory::Tenant': {
      provider: 'Azure',
      label: 'Entra ID tenant',
      key: 'Azure::ActiveDirectory::Tenant',
    },
    'Azure::ActiveDirectory::User': {
      provider: 'Azure',
      label: 'Entra ID user',
      key: 'Azure::ActiveDirectory::User',
    },
    'Azure::APIManagement::ApiOperation': {
      provider: 'Azure',
      label: 'API Management API operation',
      key: 'Azure::APIManagement::ApiOperation',
    },
    'Azure::APIManagement::Service': {
      provider: 'Azure',
      label: 'API Management service',
      key: 'Azure::APIManagement::Service',
    },
    'Azure::AppInsight::Component': {
      provider: 'Azure',
      label: 'Application Insights resource',
      key: 'Azure::AppInsight::Component',
    },
    'Azure::AppServices::KuduEndpoint': {
      provider: 'Azure',
      label: 'App Service Kudu endpoint',
      key: 'Azure::AppServices::KuduEndpoint',
    },
    'Azure::AppServices::WebApp': {
      provider: 'Azure',
      label: 'App Service web app',
      key: 'Azure::AppServices::WebApp',
    },
    'Azure::Backup::DataProtection': {
      provider: 'Azure',
      label: 'Backup vault',
      key: 'Azure::Backup::DataProtection',
    },
    'Azure::Batch::Account': {
      provider: 'Azure',
      label: 'Batch account',
      key: 'Azure::Batch::Account',
    },
    'Azure::Batch::Pool': {
      provider: 'Azure',
      label: 'Batch pool',
      key: 'Azure::Batch::Pool',
    },
    'Azure::Container::App': {
      provider: 'Azure',
      label: 'Container app',
      key: 'Azure::Container::App',
    },
    'Azure::Container::Cluster': {
      provider: 'Azure',
      label: 'AKS cluster',
      key: 'Azure::Container::Cluster',
    },
    'Azure::Container::Instance': {
      provider: 'Azure',
      label: 'Container instance',
      key: 'Azure::Container::Instance',
    },
    'Azure::Container::ManagedEnvironment': {
      provider: 'Azure',
      label: 'Container Apps environment',
      key: 'Azure::Container::ManagedEnvironment',
    },
    'Azure::Container::Registry': {
      provider: 'Azure',
      label: 'Container registry',
      key: 'Azure::Container::Registry',
    },
    'Azure::CosmosDB::DatabaseAccount': {
      provider: 'Azure',
      label: 'Cosmos DB account',
      key: 'Azure::CosmosDB::DatabaseAccount',
    },
    'Azure::DataExplorer::Cluster': {
      provider: 'Azure',
      label: 'Azure Data Explorer cluster',
      key: 'Azure::DataExplorer::Cluster',
    },
    'Azure::DataFactory::Factory': {
      provider: 'Azure',
      label: 'Data factory',
      key: 'Azure::DataFactory::Factory',
    },
    'Azure::Defender::AutoProvisioning': {
      provider: 'Azure',
      label: 'Defender for Cloud auto-provisioning setting',
      key: 'Azure::Defender::AutoProvisioning',
    },
    'Azure::Defender::Pricing': {
      provider: 'Azure',
      label: 'Defender for Cloud plan',
      key: 'Azure::Defender::Pricing',
    },
    'Azure::Defender::SecurityContact': {
      provider: 'Azure',
      label: 'Defender for Cloud security contact',
      key: 'Azure::Defender::SecurityContact',
    },
    'Azure::EventGrid::Domain': {
      provider: 'Azure',
      label: 'Event Grid domain',
      key: 'Azure::EventGrid::Domain',
    },
    'Azure::EventGrid::Topic': {
      provider: 'Azure',
      label: 'Event Grid topic',
      key: 'Azure::EventGrid::Topic',
    },
    'Azure::FunctionApp::App': {
      provider: 'Azure',
      label: 'Function app',
      key: 'Azure::FunctionApp::App',
    },
    'Azure::FunctionApp::Function': {
      provider: 'Azure',
      label: 'Function app function',
      key: 'Azure::FunctionApp::Function',
    },
    'Azure::FunctionApp::KuduEndpoint': {
      provider: 'Azure',
      label: 'Function app Kudu endpoint',
      key: 'Azure::FunctionApp::KuduEndpoint',
    },
    'Azure::ImageGallery::Gallery': {
      provider: 'Azure',
      label: 'Compute gallery',
      key: 'Azure::ImageGallery::Gallery',
    },
    'Azure::KeyVault::Certificate': {
      provider: 'Azure',
      label: 'Key Vault certificate',
      key: 'Azure::KeyVault::Certificate',
    },
    'Azure::KeyVault::Key': {
      provider: 'Azure',
      label: 'Key Vault key',
      key: 'Azure::KeyVault::Key',
    },
    'Azure::KeyVault::Secret': {
      provider: 'Azure',
      label: 'Key Vault secret',
      key: 'Azure::KeyVault::Secret',
    },
    'Azure::KeyVault::Vault': {
      provider: 'Azure',
      label: 'Key vault',
      key: 'Azure::KeyVault::Vault',
    },
    'Azure::MachineLearning::Workspace': {
      provider: 'Azure',
      label: 'Azure Machine Learning workspace',
      key: 'Azure::MachineLearning::Workspace',
    },
    'Azure::Monitor::ActivityLogAlert': {
      provider: 'Azure',
      label: 'Activity log alert rule',
      key: 'Azure::Monitor::ActivityLogAlert',
    },
    'Azure::Monitor::DiagnosticSetting': {
      provider: 'Azure',
      label: 'Activity log diagnostic setting',
      key: 'Azure::Monitor::DiagnosticSetting',
    },
    'Azure::Monitor::LogAnalyticsWorkspace': {
      provider: 'Azure',
      label: 'Log Analytics workspace',
      key: 'Azure::Monitor::LogAnalyticsWorkspace',
    },
    'Azure::Monitor::LogProfile': {
      provider: 'Azure',
      label: 'Activity log profile',
      key: 'Azure::Monitor::LogProfile',
    },
    'Azure::MySQL::FlexibleServer': {
      provider: 'Azure',
      label: 'MySQL flexible server',
      key: 'Azure::MySQL::FlexibleServer',
    },
    'Azure::MySQL::Server': {
      provider: 'Azure',
      label: 'MySQL single server',
      key: 'Azure::MySQL::Server',
    },
    'Azure::Network::Bastion': {
      provider: 'Azure',
      label: 'Bastion host',
      key: 'Azure::Network::Bastion',
    },
    'Azure::Network::NetworkInterface': {
      provider: 'Azure',
      label: 'Network interface',
      key: 'Azure::Network::NetworkInterface',
    },
    'Azure::Network::PublicIPAddress': {
      provider: 'Azure',
      label: 'Public IP address',
      key: 'Azure::Network::PublicIPAddress',
    },
    'Azure::Network::SecurityGroup': {
      provider: 'Azure',
      label: 'Network security group',
      key: 'Azure::Network::SecurityGroup',
    },
    'Azure::Network::VirtualNetwork': {
      provider: 'Azure',
      label: 'Virtual network',
      key: 'Azure::Network::VirtualNetwork',
    },
    'Azure::Network::Watcher': {
      provider: 'Azure',
      label: 'Network Watcher',
      key: 'Azure::Network::Watcher',
    },
    'Azure::PostgreSQL::FlexibleServer': {
      provider: 'Azure',
      label: 'PostgreSQL flexible server',
      key: 'Azure::PostgreSQL::FlexibleServer',
    },
    'Azure::PostgreSQL::Server': {
      provider: 'Azure',
      label: 'PostgreSQL single server',
      key: 'Azure::PostgreSQL::Server',
    },
    'Azure::RedHatOpenShift::Cluster': {
      provider: 'Azure',
      label: 'Azure Red Hat OpenShift cluster',
      key: 'Azure::RedHatOpenShift::Cluster',
    },
    'Azure::Redis::Cache': {
      provider: 'Azure',
      label: 'Redis cache',
      key: 'Azure::Redis::Cache',
    },
    'Azure::Redis::EnterpriseCache': {
      provider: 'Azure',
      label: 'Managed Redis cache',
      key: 'Azure::Redis::EnterpriseCache',
    },
    'Azure::ResourceGraph::SKU': {
      provider: 'Azure',
      label: 'Basic or Consumption SKU resource',
      key: 'Azure::ResourceGraph::SKU',
    },
    'Azure::ResourceSubscription::Location': {
      provider: 'Azure',
      label: 'Subscription region',
      key: 'Azure::ResourceSubscription::Location',
    },
    'Azure::ServiceFabric::Cluster': {
      provider: 'Azure',
      label: 'Service Fabric cluster',
      key: 'Azure::ServiceFabric::Cluster',
    },
    'Azure::ServiceFabric::ManagedCluster': {
      provider: 'Azure',
      label: 'Service Fabric managed cluster',
      key: 'Azure::ServiceFabric::ManagedCluster',
    },
    'Azure::Sql::Database': {
      provider: 'Azure',
      label: 'SQL database',
      key: 'Azure::Sql::Database',
    },
    'Azure::Sql::ManagedInstance': {
      provider: 'Azure',
      label: 'SQL managed instance',
      key: 'Azure::Sql::ManagedInstance',
    },
    'Azure::Sql::Server': {
      provider: 'Azure',
      label: 'SQL server',
      key: 'Azure::Sql::Server',
    },
    'Azure::Sql::SqlVirtualMachine': {
      provider: 'Azure',
      label: 'SQL virtual machine',
      key: 'Azure::Sql::SqlVirtualMachine',
    },
    'Azure::Storage::Container': {
      provider: 'Azure',
      label: 'Blob container',
      key: 'Azure::Storage::Container',
    },
    'Azure::Storage::StorageAccount': {
      provider: 'Azure',
      label: 'Storage account',
      key: 'Azure::Storage::StorageAccount',
    },
    'Azure::Storage::Table': {
      provider: 'Azure',
      label: 'Storage table',
      key: 'Azure::Storage::Table',
    },
    'Azure::SynapseAnalytics::Workspace': {
      provider: 'Azure',
      label: 'Synapse Analytics workspace',
      key: 'Azure::SynapseAnalytics::Workspace',
    },
    'Azure::VirtualMachine::Disk': {
      provider: 'Azure',
      label: 'Managed disk',
      key: 'Azure::VirtualMachine::Disk',
    },
    'Azure::VirtualMachine::Instance': {
      provider: 'Azure',
      label: 'Virtual machine',
      key: 'Azure::VirtualMachine::Instance',
    },
    'Azure::VirtualMachine::ScaleSet': {
      provider: 'Azure',
      label: 'Virtual machine scale set',
      key: 'Azure::VirtualMachine::ScaleSet',
    },
    'Azure::VirtualMachine::Snapshot': {
      provider: 'Azure',
      label: 'Managed disk snapshot',
      key: 'Azure::VirtualMachine::Snapshot',
    },
  },
  GCP: {
    'GCP::AccessContextManager::ServicePerimeter': {
      provider: 'GCP',
      label: 'VPC Service Controls perimeter',
      key: 'GCP::AccessContextManager::ServicePerimeter',
    },
    'GCP::AlloyDB::Cluster': {
      provider: 'GCP',
      label: 'AlloyDB cluster',
      key: 'GCP::AlloyDB::Cluster',
    },
    'GCP::AlloyDB::Instance': {
      provider: 'GCP',
      label: 'AlloyDB instance',
      key: 'GCP::AlloyDB::Instance',
    },
    'GCP::ApiKey::Key': {
      provider: 'GCP',
      label: 'API key',
      key: 'GCP::ApiKey::Key',
    },
    'GCP::Batch::Job': {
      provider: 'GCP',
      label: 'Batch job',
      key: 'GCP::Batch::Job',
    },
    'GCP::BigQuery::Dataset': {
      provider: 'GCP',
      label: 'BigQuery dataset',
      key: 'GCP::BigQuery::Dataset',
    },
    'GCP::BigQuery::Table': {
      provider: 'GCP',
      label: 'BigQuery table',
      key: 'GCP::BigQuery::Table',
    },
    'GCP::CloudAICompanion::DataSharingSetting': {
      provider: 'GCP',
      label: 'Gemini data sharing setting',
      key: 'GCP::CloudAICompanion::DataSharingSetting',
    },
    'GCP::CloudComposer::Environment': {
      provider: 'GCP',
      label: 'Cloud Composer environment',
      key: 'GCP::CloudComposer::Environment',
    },
    'GCP::CloudFunctions::Function': {
      provider: 'GCP',
      label: 'Cloud Run function',
      key: 'GCP::CloudFunctions::Function',
    },
    'GCP::CloudKMS::Key': {
      provider: 'GCP',
      label: 'Cloud KMS key',
      key: 'GCP::CloudKMS::Key',
    },
    'GCP::CloudRun::Service': {
      provider: 'GCP',
      label: 'Cloud Run service',
      key: 'GCP::CloudRun::Service',
    },
    'GCP::CloudTPU::Node': {
      provider: 'GCP',
      label: 'Cloud TPU node',
      key: 'GCP::CloudTPU::Node',
    },
    'GCP::Compute::Address': {
      provider: 'GCP',
      label: 'Compute Engine IP address',
      key: 'GCP::Compute::Address',
    },
    'GCP::Compute::BackendBucket': {
      provider: 'GCP',
      label: 'Cloud Load Balancing backend bucket',
      key: 'GCP::Compute::BackendBucket',
    },
    'GCP::Compute::BackendService': {
      provider: 'GCP',
      label: 'Cloud Load Balancing backend service',
      key: 'GCP::Compute::BackendService',
    },
    'GCP::Compute::Disk': {
      provider: 'GCP',
      label: 'Compute Engine disk',
      key: 'GCP::Compute::Disk',
    },
    'GCP::Compute::Firewall': {
      provider: 'GCP',
      label: 'VPC firewall rule',
      key: 'GCP::Compute::Firewall',
    },
    'GCP::Compute::ForwardingRule': {
      provider: 'GCP',
      label: 'Cloud Load Balancing forwarding rule',
      key: 'GCP::Compute::ForwardingRule',
    },
    'GCP::Compute::Image': {
      provider: 'GCP',
      label: 'Compute Engine image',
      key: 'GCP::Compute::Image',
    },
    'GCP::Compute::Instance': {
      provider: 'GCP',
      label: 'Compute Engine VM instance',
      key: 'GCP::Compute::Instance',
    },
    'GCP::Compute::InstanceGroupManager': {
      provider: 'GCP',
      label: 'Compute Engine managed instance group',
      key: 'GCP::Compute::InstanceGroupManager',
    },
    'GCP::Compute::InstanceTemplate': {
      provider: 'GCP',
      label: 'Compute Engine instance template',
      key: 'GCP::Compute::InstanceTemplate',
    },
    'GCP::Compute::Network': {
      provider: 'GCP',
      label: 'VPC network',
      key: 'GCP::Compute::Network',
    },
    'GCP::Compute::NetworkFirewallPolicy': {
      provider: 'GCP',
      label: 'VPC network firewall policy',
      key: 'GCP::Compute::NetworkFirewallPolicy',
    },
    'GCP::Compute::Project': {
      provider: 'GCP',
      label: 'Compute Engine project metadata',
      key: 'GCP::Compute::Project',
    },
    'GCP::Compute::RegionNetworkEndpointGroup': {
      provider: 'GCP',
      label: 'Regional network endpoint group',
      key: 'GCP::Compute::RegionNetworkEndpointGroup',
    },
    'GCP::Compute::RegionNetworkFirewallPolicy': {
      provider: 'GCP',
      label: 'VPC regional network firewall policy',
      key: 'GCP::Compute::RegionNetworkFirewallPolicy',
    },
    'GCP::Compute::Snapshot': {
      provider: 'GCP',
      label: 'Compute Engine disk snapshot',
      key: 'GCP::Compute::Snapshot',
    },
    'GCP::Compute::Subnetwork': {
      provider: 'GCP',
      label: 'VPC subnet',
      key: 'GCP::Compute::Subnetwork',
    },
    'GCP::Dataflow::Job': {
      provider: 'GCP',
      label: 'Dataflow job',
      key: 'GCP::Dataflow::Job',
    },
    'GCP::DataProc::Cluster': {
      provider: 'GCP',
      label: 'Dataproc cluster',
      key: 'GCP::DataProc::Cluster',
    },
    'GCP::Datastream::ConnectionProfile': {
      provider: 'GCP',
      label: 'Datastream connection profile',
      key: 'GCP::Datastream::ConnectionProfile',
    },
    'GCP::Dialogflow::Agent': {
      provider: 'GCP',
      label: 'Dialogflow CX agent',
      key: 'GCP::Dialogflow::Agent',
    },
    'GCP::Dialogflow::SecuritySettings': {
      provider: 'GCP',
      label: 'Dialogflow CX security settings',
      key: 'GCP::Dialogflow::SecuritySettings',
    },
    'GCP::DiscoveryEngine::Agent': {
      provider: 'GCP',
      label: 'Gemini Enterprise agent',
      key: 'GCP::DiscoveryEngine::Agent',
    },
    'GCP::DiscoveryEngine::Authorization': {
      provider: 'GCP',
      label: 'Gemini Enterprise authorization',
      key: 'GCP::DiscoveryEngine::Authorization',
    },
    'GCP::DiscoveryEngine::DataStore': {
      provider: 'GCP',
      label: 'Vertex AI Search data store',
      key: 'GCP::DiscoveryEngine::DataStore',
    },
    'GCP::DiscoveryEngine::Engine': {
      provider: 'GCP',
      label: 'Vertex AI Search app',
      key: 'GCP::DiscoveryEngine::Engine',
    },
    'GCP::DNS::ManagedZone': {
      provider: 'GCP',
      label: 'Cloud DNS managed zone',
      key: 'GCP::DNS::ManagedZone',
    },
    'GCP::DNS::Policy': {
      provider: 'GCP',
      label: 'Cloud DNS server policy',
      key: 'GCP::DNS::Policy',
    },
    'GCP::DNS::ResourceRecordSet': {
      provider: 'GCP',
      label: 'Cloud DNS record set',
      key: 'GCP::DNS::ResourceRecordSet',
    },
    'GCP::DocumentAI::Processor': {
      provider: 'GCP',
      label: 'Document AI processor',
      key: 'GCP::DocumentAI::Processor',
    },
    'GCP::Filestore::Instance': {
      provider: 'GCP',
      label: 'Filestore instance',
      key: 'GCP::Filestore::Instance',
    },
    'GCP::Firestore::Database': {
      provider: 'GCP',
      label: 'Firestore database',
      key: 'GCP::Firestore::Database',
    },
    'GCP::GKE::Cluster': {
      provider: 'GCP',
      label: 'GKE cluster',
      key: 'GCP::GKE::Cluster',
    },
    'GCP::IAM::AuditLogs': {
      provider: 'GCP',
      label: 'Cloud Audit Logs setting',
      key: 'GCP::IAM::AuditLogs',
    },
    'GCP::IAM::EssentialContact': {
      provider: 'GCP',
      label: 'Essential contact',
      key: 'GCP::IAM::EssentialContact',
    },
    'GCP::IAM::FolderPolicy': {
      provider: 'GCP',
      label: 'Folder IAM policy',
      key: 'GCP::IAM::FolderPolicy',
    },
    'GCP::IAM::OrganizationPolicy': {
      provider: 'GCP',
      label: 'Organization IAM policy',
      key: 'GCP::IAM::OrganizationPolicy',
    },
    'GCP::IAM::OrganizationRole': {
      provider: 'GCP',
      label: 'Organization custom role',
      key: 'GCP::IAM::OrganizationRole',
    },
    'GCP::IAM::ProjectPolicy': {
      provider: 'GCP',
      label: 'Project IAM policy',
      key: 'GCP::IAM::ProjectPolicy',
    },
    'GCP::IAM::Role': {
      provider: 'GCP',
      label: 'Custom role',
      key: 'GCP::IAM::Role',
    },
    'GCP::IAM::ServiceAccount': {
      provider: 'GCP',
      label: 'IAM service account',
      key: 'GCP::IAM::ServiceAccount',
    },
    'GCP::IAM::ServiceAccountKey': {
      provider: 'GCP',
      label: 'IAM service account key',
      key: 'GCP::IAM::ServiceAccountKey',
    },
    'GCP::IAM::ServiceAccountPolicy': {
      provider: 'GCP',
      label: 'Service account IAM policy',
      key: 'GCP::IAM::ServiceAccountPolicy',
    },
    'GCP::IAM::WorkloadIdentityPool': {
      provider: 'GCP',
      label: 'Workload identity pool',
      key: 'GCP::IAM::WorkloadIdentityPool',
    },
    'GCP::IAM::WorkloadIdentityPoolProvider': {
      provider: 'GCP',
      label: 'Workload identity pool provider',
      key: 'GCP::IAM::WorkloadIdentityPoolProvider',
    },
    'GCP::Logging::LogBucket': {
      provider: 'GCP',
      label: 'Cloud Logging log bucket',
      key: 'GCP::Logging::LogBucket',
    },
    'GCP::Logging::LogsRouterSink': {
      provider: 'GCP',
      label: 'Cloud Logging sink',
      key: 'GCP::Logging::LogsRouterSink',
    },
    'GCP::Logging::ProjectMetrics': {
      provider: 'GCP',
      label: 'Cloud Logging log-based metrics',
      key: 'GCP::Logging::ProjectMetrics',
    },
    'GCP::ModelArmor::FloorSetting': {
      provider: 'GCP',
      label: 'Model Armor floor setting',
      key: 'GCP::ModelArmor::FloorSetting',
    },
    'GCP::ModelArmor::Template': {
      provider: 'GCP',
      label: 'Model Armor template',
      key: 'GCP::ModelArmor::Template',
    },
    'GCP::Monitoring::AccessApproval': {
      provider: 'GCP',
      label: 'Access Approval setting',
      key: 'GCP::Monitoring::AccessApproval',
    },
    'GCP::Monitoring::ProjectAlertPolicies': {
      provider: 'GCP',
      label: 'Cloud Monitoring alerting policies',
      key: 'GCP::Monitoring::ProjectAlertPolicies',
    },
    'GCP::Monitoring::Services': {
      provider: 'GCP',
      label: 'Enabled APIs and services',
      key: 'GCP::Monitoring::Services',
    },
    'GCP::OrgPolicy::Policy': {
      provider: 'GCP',
      label: 'Organization policy',
      key: 'GCP::OrgPolicy::Policy',
    },
    'GCP::ResourceManager::Folder': {
      provider: 'GCP',
      label: 'Folder',
      key: 'GCP::ResourceManager::Folder',
    },
    'GCP::ResourceManager::Organization': {
      provider: 'GCP',
      label: 'Organization',
      key: 'GCP::ResourceManager::Organization',
    },
    'GCP::ResourceManager::OrgPolicy': {
      provider: 'GCP',
      label: 'Project organization policy',
      key: 'GCP::ResourceManager::OrgPolicy',
    },
    'GCP::ResourceManager::Project': {
      provider: 'GCP',
      label: 'Project',
      key: 'GCP::ResourceManager::Project',
    },
    'GCP::SecretManager::Secret': {
      provider: 'GCP',
      label: 'Secret Manager secret',
      key: 'GCP::SecretManager::Secret',
    },
    'GCP::Spanner::Database': {
      provider: 'GCP',
      label: 'Spanner database',
      key: 'GCP::Spanner::Database',
    },
    'GCP::Spanner::Instance': {
      provider: 'GCP',
      label: 'Spanner instance',
      key: 'GCP::Spanner::Instance',
    },
    'GCP::SQL::Instance': {
      provider: 'GCP',
      label: 'Cloud SQL instance',
      key: 'GCP::SQL::Instance',
    },
    'GCP::Storage::Bucket': {
      provider: 'GCP',
      label: 'Cloud Storage bucket',
      key: 'GCP::Storage::Bucket',
    },
    'GCP::VertexAI::BatchPredictionJob': {
      provider: 'GCP',
      label: 'Vertex AI batch prediction job',
      key: 'GCP::VertexAI::BatchPredictionJob',
    },
    'GCP::VertexAI::CustomJob': {
      provider: 'GCP',
      label: 'Vertex AI custom training job',
      key: 'GCP::VertexAI::CustomJob',
    },
    'GCP::VertexAI::Endpoint': {
      provider: 'GCP',
      label: 'Vertex AI endpoint',
      key: 'GCP::VertexAI::Endpoint',
    },
    'GCP::VertexAI::FeatureOnlineStore': {
      provider: 'GCP',
      label: 'Vertex AI feature online store',
      key: 'GCP::VertexAI::FeatureOnlineStore',
    },
    'GCP::VertexAI::Featurestore': {
      provider: 'GCP',
      label: 'Vertex AI Feature Store (legacy)',
      key: 'GCP::VertexAI::Featurestore',
    },
    'GCP::VertexAI::IndexEndpoint': {
      provider: 'GCP',
      label: 'Vertex AI Vector Search index endpoint',
      key: 'GCP::VertexAI::IndexEndpoint',
    },
    'GCP::VertexAI::Model': {
      provider: 'GCP',
      label: 'Vertex AI model',
      key: 'GCP::VertexAI::Model',
    },
    'GCP::VertexAI::NotebookRuntimeTemplate': {
      provider: 'GCP',
      label: 'Colab Enterprise runtime template',
      key: 'GCP::VertexAI::NotebookRuntimeTemplate',
    },
    'GCP::VertexAI::PipelineJob': {
      provider: 'GCP',
      label: 'Vertex AI pipeline run',
      key: 'GCP::VertexAI::PipelineJob',
    },
    'GCP::VertexAI::RagCorpus': {
      provider: 'GCP',
      label: 'Vertex AI RAG corpus',
      key: 'GCP::VertexAI::RagCorpus',
    },
    'GCP::VertexAI::ReasoningEngine': {
      provider: 'GCP',
      label: 'Vertex AI Agent Engine agent',
      key: 'GCP::VertexAI::ReasoningEngine',
    },
    'GCP::VertexAI::Runtime': {
      provider: 'GCP',
      label: 'Vertex AI Workbench runtime',
      key: 'GCP::VertexAI::Runtime',
    },
    'GCP::VertexAI::Tensorboard': {
      provider: 'GCP',
      label: 'Vertex AI TensorBoard instance',
      key: 'GCP::VertexAI::Tensorboard',
    },
    'GCP::VertexAI::TuningJob': {
      provider: 'GCP',
      label: 'Vertex AI tuning job',
      key: 'GCP::VertexAI::TuningJob',
    },
    'GCP::VertexAI::WorkbenchInstance': {
      provider: 'GCP',
      label: 'Vertex AI Workbench instance',
      key: 'GCP::VertexAI::WorkbenchInstance',
    },
    'GCP::Workstations::WorkstationConfig': {
      provider: 'GCP',
      label: 'Cloud Workstations configuration',
      key: 'GCP::Workstations::WorkstationConfig',
    },
  },
  Kubernetes: {
    'Kubernetes::Auth::ClusterRole': {
      provider: 'Kubernetes',
      label: 'ClusterRole',
      key: 'Kubernetes::Auth::ClusterRole',
    },
    'Kubernetes::Auth::ClusterRoleBinding': {
      provider: 'Kubernetes',
      label: 'ClusterRoleBinding',
      key: 'Kubernetes::Auth::ClusterRoleBinding',
    },
    'Kubernetes::Auth::Role': {
      provider: 'Kubernetes',
      label: 'Role',
      key: 'Kubernetes::Auth::Role',
    },
    'Kubernetes::Auth::RoleBinding': {
      provider: 'Kubernetes',
      label: 'RoleBinding',
      key: 'Kubernetes::Auth::RoleBinding',
    },
    'Kubernetes::Auth::ServiceAccount': {
      provider: 'Kubernetes',
      label: 'ServiceAccount',
      key: 'Kubernetes::Auth::ServiceAccount',
    },
    'Kubernetes::Cluster::APIService': {
      provider: 'Kubernetes',
      label: 'APIService',
      key: 'Kubernetes::Cluster::APIService',
    },
    'Kubernetes::Cluster::FlowSchema': {
      provider: 'Kubernetes',
      label: 'FlowSchema',
      key: 'Kubernetes::Cluster::FlowSchema',
    },
    'Kubernetes::Cluster::Lease': {
      provider: 'Kubernetes',
      label: 'Lease',
      key: 'Kubernetes::Cluster::Lease',
    },
    'Kubernetes::Cluster::Namespace': {
      provider: 'Kubernetes',
      label: 'Namespace',
      key: 'Kubernetes::Cluster::Namespace',
    },
    'Kubernetes::Cluster::Node': {
      provider: 'Kubernetes',
      label: 'Node',
      key: 'Kubernetes::Cluster::Node',
    },
    'Kubernetes::Cluster::PriorityLevelConfiguration': {
      provider: 'Kubernetes',
      label: 'PriorityLevelConfiguration',
      key: 'Kubernetes::Cluster::PriorityLevelConfiguration',
    },
    'Kubernetes::Cluster::RuntimeClass': {
      provider: 'Kubernetes',
      label: 'RuntimeClass',
      key: 'Kubernetes::Cluster::RuntimeClass',
    },
    'Kubernetes::Config::ConfigMap': {
      provider: 'Kubernetes',
      label: 'ConfigMap',
      key: 'Kubernetes::Config::ConfigMap',
    },
    'Kubernetes::Config::EndpointSlice': {
      provider: 'Kubernetes',
      label: 'EndpointSlice',
      key: 'Kubernetes::Config::EndpointSlice',
    },
    'Kubernetes::Config::Secret': {
      provider: 'Kubernetes',
      label: 'Secret',
      key: 'Kubernetes::Config::Secret',
    },
    'Kubernetes::Ext::CustomResourceDefinition': {
      provider: 'Kubernetes',
      label: 'CustomResourceDefinition',
      key: 'Kubernetes::Ext::CustomResourceDefinition',
    },
    'Kubernetes::Ext::MutatingWebhookConfiguration': {
      provider: 'Kubernetes',
      label: 'MutatingWebhookConfiguration',
      key: 'Kubernetes::Ext::MutatingWebhookConfiguration',
    },
    'Kubernetes::Ext::ValidatingWebhookConfiguration': {
      provider: 'Kubernetes',
      label: 'ValidatingWebhookConfiguration',
      key: 'Kubernetes::Ext::ValidatingWebhookConfiguration',
    },
    'Kubernetes::Plerion::Integrations': {
      provider: 'Kubernetes',
      label: 'Plerion integration',
      key: 'Kubernetes::Plerion::Integrations',
    },
    'Kubernetes::Policy::LimitRange': {
      provider: 'Kubernetes',
      label: 'LimitRange',
      key: 'Kubernetes::Policy::LimitRange',
    },
    'Kubernetes::Policy::NetworkPolicy': {
      provider: 'Kubernetes',
      label: 'NetworkPolicy',
      key: 'Kubernetes::Policy::NetworkPolicy',
    },
    'Kubernetes::Policy::PodDistributionBudget': {
      provider: 'Kubernetes',
      label: 'PodDisruptionBudget',
      key: 'Kubernetes::Policy::PodDistributionBudget',
    },
    'Kubernetes::Policy::ResourceQuota': {
      provider: 'Kubernetes',
      label: 'ResourceQuota',
      key: 'Kubernetes::Policy::ResourceQuota',
    },
    'Kubernetes::Service::Endpoint': {
      provider: 'Kubernetes',
      label: 'Endpoints',
      key: 'Kubernetes::Service::Endpoint',
    },
    'Kubernetes::Service::EndpointSlice': {
      provider: 'Kubernetes',
      label: 'EndpointSlice',
      key: 'Kubernetes::Service::EndpointSlice',
    },
    'Kubernetes::Service::Ingress': {
      provider: 'Kubernetes',
      label: 'Ingress',
      key: 'Kubernetes::Service::Ingress',
    },
    'Kubernetes::Service::IngressClass': {
      provider: 'Kubernetes',
      label: 'IngressClass',
      key: 'Kubernetes::Service::IngressClass',
    },
    'Kubernetes::Service::Service': {
      provider: 'Kubernetes',
      label: 'Service',
      key: 'Kubernetes::Service::Service',
    },
    'Kubernetes::Storage::CSIDriver': {
      provider: 'Kubernetes',
      label: 'CSIDriver',
      key: 'Kubernetes::Storage::CSIDriver',
    },
    'Kubernetes::Storage::CSINode': {
      provider: 'Kubernetes',
      label: 'CSINode',
      key: 'Kubernetes::Storage::CSINode',
    },
    'Kubernetes::Storage::CSIStorageCapacity': {
      provider: 'Kubernetes',
      label: 'CSIStorageCapacity',
      key: 'Kubernetes::Storage::CSIStorageCapacity',
    },
    'Kubernetes::Storage::PersistentVolume': {
      provider: 'Kubernetes',
      label: 'PersistentVolume',
      key: 'Kubernetes::Storage::PersistentVolume',
    },
    'Kubernetes::Storage::PersistentVolumeClaim': {
      provider: 'Kubernetes',
      label: 'PersistentVolumeClaim',
      key: 'Kubernetes::Storage::PersistentVolumeClaim',
    },
    'Kubernetes::Storage::StorageClass': {
      provider: 'Kubernetes',
      label: 'StorageClass',
      key: 'Kubernetes::Storage::StorageClass',
    },
    'Kubernetes::Storage::VolumeAttachment': {
      provider: 'Kubernetes',
      label: 'VolumeAttachment',
      key: 'Kubernetes::Storage::VolumeAttachment',
    },
    'Kubernetes::Workload::CronJob': {
      provider: 'Kubernetes',
      label: 'CronJob',
      key: 'Kubernetes::Workload::CronJob',
    },
    'Kubernetes::Workload::DaemonSet': {
      provider: 'Kubernetes',
      label: 'DaemonSet',
      key: 'Kubernetes::Workload::DaemonSet',
    },
    'Kubernetes::Workload::Deployment': {
      provider: 'Kubernetes',
      label: 'Deployment',
      key: 'Kubernetes::Workload::Deployment',
    },
    'Kubernetes::Workload::HorizontalPodAutoscaler': {
      provider: 'Kubernetes',
      label: 'HorizontalPodAutoscaler',
      key: 'Kubernetes::Workload::HorizontalPodAutoscaler',
    },
    'Kubernetes::Workload::Job': {
      provider: 'Kubernetes',
      label: 'Job',
      key: 'Kubernetes::Workload::Job',
    },
    'Kubernetes::Workload::Pod': {
      provider: 'Kubernetes',
      label: 'Pod',
      key: 'Kubernetes::Workload::Pod',
    },
    'Kubernetes::Workload::PodTemplate': {
      provider: 'Kubernetes',
      label: 'PodTemplate',
      key: 'Kubernetes::Workload::PodTemplate',
    },
    'Kubernetes::Workload::PriorityClass': {
      provider: 'Kubernetes',
      label: 'PriorityClass',
      key: 'Kubernetes::Workload::PriorityClass',
    },
    'Kubernetes::Workload::ReplicaSet': {
      provider: 'Kubernetes',
      label: 'ReplicaSet',
      key: 'Kubernetes::Workload::ReplicaSet',
    },
    'Kubernetes::Workload::ReplicationController': {
      provider: 'Kubernetes',
      label: 'ReplicationController',
      key: 'Kubernetes::Workload::ReplicationController',
    },
    'Kubernetes::Workload::StatefulSet': {
      provider: 'Kubernetes',
      label: 'StatefulSet',
      key: 'Kubernetes::Workload::StatefulSet',
    },
  },
  Code: {
    'Code::Bicep::Template': {
      provider: 'Code',
      label: 'Bicep file',
      key: 'Code::Bicep::Template',
    },
    'Code::Bun::Manifest': {
      provider: 'Code',
      label: 'Bun manifest',
      key: 'Code::Bun::Manifest',
    },
    'Code::Bundler::Manifest': {
      provider: 'Code',
      label: 'Bundler Gemfile',
      key: 'Code::Bundler::Manifest',
    },
    'Code::Cargo::Manifest': {
      provider: 'Code',
      label: 'Cargo manifest',
      key: 'Code::Cargo::Manifest',
    },
    'Code::CDK::File': {
      provider: 'Code',
      label: 'CDK file',
      key: 'Code::CDK::File',
    },
    'Code::CloudFormation::Template': {
      provider: 'Code',
      label: 'CloudFormation template',
      key: 'Code::CloudFormation::Template',
    },
    'Code::Cocoapods::Manifest': {
      provider: 'Code',
      label: 'CocoaPods Podfile',
      key: 'Code::Cocoapods::Manifest',
    },
    'Code::Composer::Manifest': {
      provider: 'Code',
      label: 'Composer manifest',
      key: 'Code::Composer::Manifest',
    },
    'Code::Conan::Manifest': {
      provider: 'Code',
      label: 'Conan manifest',
      key: 'Code::Conan::Manifest',
    },
    'Code::Git::Repository': {
      provider: 'Code',
      label: 'Git repository',
      key: 'Code::Git::Repository',
    },
    'Code::Gomod::Manifest': {
      provider: 'Code',
      label: 'go.mod file',
      key: 'Code::Gomod::Manifest',
    },
    'Code::Gradle::Manifest': {
      provider: 'Code',
      label: 'Gradle build file',
      key: 'Code::Gradle::Manifest',
    },
    'Code::Julia::Manifest': {
      provider: 'Code',
      label: 'Julia project file',
      key: 'Code::Julia::Manifest',
    },
    'Code::Mix::Manifest': {
      provider: 'Code',
      label: 'Elixir Mix manifest',
      key: 'Code::Mix::Manifest',
    },
    'Code::Npm::Manifest': {
      provider: 'Code',
      label: 'npm manifest',
      key: 'Code::Npm::Manifest',
    },
    'Code::Nuget::Manifest': {
      provider: 'Code',
      label: 'NuGet manifest',
      key: 'Code::Nuget::Manifest',
    },
    'Code::Pip::Manifest': {
      provider: 'Code',
      label: 'pip requirements file',
      key: 'Code::Pip::Manifest',
    },
    'Code::Pipenv::Manifest': {
      provider: 'Code',
      label: 'Pipenv Pipfile',
      key: 'Code::Pipenv::Manifest',
    },
    'Code::Pnpm::Manifest': {
      provider: 'Code',
      label: 'pnpm manifest',
      key: 'Code::Pnpm::Manifest',
    },
    'Code::Poetry::Manifest': {
      provider: 'Code',
      label: 'Poetry manifest',
      key: 'Code::Poetry::Manifest',
    },
    'Code::Pom::Manifest': {
      provider: 'Code',
      label: 'Maven POM',
      key: 'Code::Pom::Manifest',
    },
    'Code::Pub::Manifest': {
      provider: 'Code',
      label: 'Dart pubspec',
      key: 'Code::Pub::Manifest',
    },
    'Code::SAM::Template': {
      provider: 'Code',
      label: 'SAM template',
      key: 'Code::SAM::Template',
    },
    'Code::Sbt::Manifest': {
      provider: 'Code',
      label: 'sbt build file',
      key: 'Code::Sbt::Manifest',
    },
    'Code::Serverless::Service': {
      provider: 'Code',
      label: 'Serverless Framework service',
      key: 'Code::Serverless::Service',
    },
    'Code::Swift::Manifest': {
      provider: 'Code',
      label: 'Swift package manifest',
      key: 'Code::Swift::Manifest',
    },
    'Code::Terraform::File': {
      provider: 'Code',
      label: 'Terraform file',
      key: 'Code::Terraform::File',
    },
    'Code::Yarn::Manifest': {
      provider: 'Code',
      label: 'Yarn manifest',
      key: 'Code::Yarn::Manifest',
    },
  },
};

const byLowerKey: Record<string, ResourceType> = Object.fromEntries(
  Object.values(RESOURCE_TYPE_MAP).flatMap((types) =>
    Object.values(types).map((type) => [type.key.toLowerCase(), type]),
  ),
);

/**
 * A resource type's friendly name, else the stored type as it is, so a type
 * added before the map knows it still shows. The lookup ignores case because
 * some collectors store a type with different casing from the registry
 * (Kubernetes::Cluster::ApiService).
 */
export const resourceTypeLabel = (type: string | undefined | null): string =>
  type ? (byLowerKey[type.toLowerCase()]?.label ?? type) : '';
